import { useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { MenuItem, Typography } from '@mui/material';

import { defaultLanguage } from 'context';
import { useCourseContext } from 'context';
import { useChoiceOptions } from 'context/choice-randomization';
import {
  SelectFormField,
  FormChoiceResponse,
  FormChoiceResponseType,
  FORM_OTHER_OPTION_VALUE,
} from 'types';

import { Input } from '../input';
import { InputField } from '../fields';

export const FormSelectField = ({
  id,
  label,
  options,
  placeholder,
  translations,
}: SelectFormField) => {
  const { language } = useCourseContext();
  const { t } = useTranslation('common');
  const otherLabel = t('choiceOther');
  const [field, , helpers] = useField<
    FormChoiceResponse | FormChoiceResponse[] | null
  >(id);
  const responses = Array.isArray(field.value)
    ? field.value
    : field.value
      ? [field.value]
      : [];
  const otherResponse = responses.find(
    (response) => response.type === FormChoiceResponseType.OTHER
  );
  const controlValue = responses.map((response) =>
    response.type === FormChoiceResponseType.OTHER
      ? FORM_OTHER_OPTION_VALUE
      : response.value
  );

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        placeholder,
        options: options?.options
          ? options?.options.map((value) => ({ value, label: value }))
          : [],
      };
    }

    const translation = translations[language];
    const langOptions = translation?.options || options?.options;
    return {
      label: label ? translation?.label || label : label,
      placeholder: translation?.placeholder || placeholder,
      options: langOptions
        ? langOptions.map((value, index) => ({
            label: value,
            value: options?.options?.[index] || value,
          }))
        : [],
    };
  }, [label, placeholder, options?.options, language, translations]);

  const orderedOptions = useChoiceOptions(
    id,
    options?.randomize,
    translation.options
  );

  return (
    <div className="flex flex-col gap-2">
      <InputField
        select
        name={id}
        fullWidth
        className="selectInput"
        label={translation.label}
        placeholder={translation.placeholder}
        displayValue={
          options?.multiple ? controlValue : (controlValue[0] ?? '')
        }
        mapValue={(selectedValue) => {
          const selectedValues = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue];
          const nextResponses = selectedValues.map((value) =>
            value === FORM_OTHER_OPTION_VALUE
              ? {
                  value: otherResponse?.value ?? '',
                  type: FormChoiceResponseType.OTHER,
                }
              : { value, type: FormChoiceResponseType.OPTION }
          );
          return options?.multiple ? nextResponses : (nextResponses[0] ?? null);
        }}
        slotProps={{
          select: {
            multiple: options?.multiple,
            renderValue: (selected) => {
              const values = Array.isArray(selected) ? selected : [selected];
              if (values.length === 0 || selected === '') {
                return (
                  <Typography component="span" sx={{ color: 'text.disabled' }}>
                    {translation.placeholder}
                  </Typography>
                );
              }
              return values
                .map((value) =>
                  value === FORM_OTHER_OPTION_VALUE
                    ? otherLabel
                    : (orderedOptions.find((option) => option.value === value)
                        ?.label ?? value)
                )
                .join(', ');
            },
          },
        }}
      >
        {orderedOptions &&
          orderedOptions.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        {options?.allowOther && (
          <MenuItem value={FORM_OTHER_OPTION_VALUE}>{otherLabel}</MenuItem>
        )}
      </InputField>
      {options?.allowOther && otherResponse && (
        <Input
          fullWidth
          label={otherLabel}
          value={otherResponse.value}
          placeholder={t('choiceOtherPlaceholder')}
          onBlur={() => helpers.setTouched(true, true)}
          onChange={(event) => {
            const nextOtherResponse: FormChoiceResponse = {
              value: event.target.value,
              type: FormChoiceResponseType.OTHER,
            };
            helpers.setValue(
              Array.isArray(field.value)
                ? [
                    ...field.value.filter(
                      (response) =>
                        response.type !== FormChoiceResponseType.OTHER
                    ),
                    nextOtherResponse,
                  ]
                : nextOtherResponse,
              true
            );
          }}
        />
      )}
    </div>
  );
};
